/**
 * policyManagementGuard — `CanActivateFn` factory that gates
 * `/settings/policies` and every child route on the Policy v2
 * admin actions. Implements the Ambassador pattern (S7.8): a custom
 * role derived from LOCATION_ADMIN minus `policy:read` +
 * `insights:read` must not be able to reach the Policies admin
 * surface even by deep-linking, while every other admin role
 * (Global Admin, Location Admin holding `policy:manage`, custom
 * roles holding `policy:read`) gets through unchanged.
 *
 * USAGE
 *   {
 *     path: 'policies',
 *     canActivate: [authGuard, adminGuard, policyManagementGuard()],
 *     loadComponent: () => …,
 *   }
 *
 * SEMANTICS
 *   - Waits for `ViewerService.ensureLoaded()` so the viewer-side
 *     action set is hydrated before the decision. Without the await
 *     the actions read empty on first navigation after a hard reload
 *     and the user gets bounced even when they should be allowed in.
 *   - Treats `policy:manage` as covering `policy:read` (the
 *     hierarchical matcher does this anyway, but spelling it out
 *     here keeps the intent clear).
 *   - Treats `policy:read` as sufficient on its own — read-only
 *     personas land on the tier pages, save buttons stay disabled
 *     via the existing `*imHasAction` gates.
 *   - Treats `policy:read:section` — the API's read verb, and the one a
 *     read-only policy role carries — as sufficient too. `policy:read` is
 *     not an API action; it stays admitted so a custom role spelled that
 *     way is not locked out.
 *   - Redirects to the wildcard fall-through (`/home-preview`, the
 *     default home surface) on failure to match the
 *     `featureFlagGuard` pattern — the absence of the action is
 *     indistinguishable from the route never having existed. The S7.8 spec calls this a "403" but the
 *     UX-equivalent in this app is a hard 404 (consistent with how
 *     the rest of the admin surface hides itself).
 *
 * AI CONTEXT
 *   - This guard runs in addition to the existing `adminGuard` on
 *     `/settings/**`. The two guards compose: `adminGuard` admits
 *     anyone holding `admin:*`, `policyManagementGuard` further
 *     narrows to holders of `policy:manage` / `policy:read`. The
 *     Ambassador role is the canonical case where the first guard
 *     accepts but the second rejects.
 *   - The action set check is local — no GraphQL query, no
 *     external service call. The viewer's action set already lives
 *     in `ProfileDataService.identity.actions()`; we only await
 *     `ensureLoaded()` so the set is hydrated.
 *   - The guard MUST be a factory (return a `CanActivateFn`) so the
 *     test can call it without an active injection context and the
 *     route table can `canActivate: [policyManagementGuard()]` to
 *     keep the construction site obvious.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Action } from '../constants/action-catalogue';
import { ProfileDataService } from '../services/profile-data.service';
import { ViewerService } from '../services/viewer.service';
import { isAdminPreview } from './admin-preview';

export function policyManagementGuard(): CanActivateFn {
  return async (route) => {
    const viewerService = inject(ViewerService);
    const profile = inject(ProfileDataService);
    const router = inject(Router);

    // Dev-only preview bypass (mirrors authGuard/adminGuard) so the policy
    // surfaces are reviewable locally with `?adminPreview=1`. No-op in
    // production builds (see isAdminPreview's environment short-circuit).
    if (isAdminPreview(route)) return true;

    await viewerService.ensureLoaded();

    // Either action grants access — POLICY_MANAGE is hierarchically
    // wider and already covers POLICY_READ via matchesActionHierarchical,
    // but we still check both explicitly so custom roles that hold
    // only POLICY_READ (Ambassador's positive counterpart) are
    // admitted to the read-only surfaces without a wildcard.
    const canManage = profile.canDo(Action.POLICY_MANAGE);
    // NOT an API action (the API has no plain `policy:read`) — kept so a
    // custom role that spells it this way is not locked out.
    const canRead = profile.canDo(Action.POLICY_READ);
    // The API's read verb: a child action does not satisfy `policy:read`
    // in the hierarchical matcher, so it must be named on its own.
    const canReadSection = profile.canDo(Action.POLICY_READ_SECTION);

    if (canManage || canRead || canReadSection) return true;
    // Hard 404 fall-through. Matches the featureFlagGuard contract
    // so the absence of the action is indistinguishable from the
    // route never existing — Ambassador role sees the same outcome
    // as any visitor who lands on an unknown URL.
    return router.createUrlTree(['/home-preview']);
  };
}
